import { Button, Col, Form, Row, Select, Typography } from 'antd'
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { formatMoneyValue, money, type Money } from '@/shared/lib/money'
import { MoneyInput } from '@/shared/ui/MoneyInput'
import { paymentCategoryName, type PaymentCategory } from './paymentCategoryApi'

const MAX_SPLIT_LINES = 10

export interface SplitLine {
  categoryId?: string
  amount: Money | null
}

interface CategorySplitProps {
  categories: readonly PaymentCategory[]
  lines: readonly (SplitLine | undefined)[]
  total: number
  currency: string
}

function splitSum(lines: readonly (SplitLine | undefined)[]): number {
  return lines.reduce((sum, line) => sum + (line?.amount?.amount ?? 0), 0)
}

export function CategorySplit({ categories, lines, total, currency }: CategorySplitProps) {
  const { t } = useTranslation()
  const sum = splitSum(lines)
  const chosen = new Set(lines.map((line) => line?.categoryId).filter(Boolean))

  return (
    <Form.List
      name="lines"
      rules={[
        {
          validator: (_, value: SplitLine[] | undefined) =>
            !value?.length || splitSum(value) === total
              ? Promise.resolve()
              : Promise.reject(new Error(t('cashdesk.split.mismatch'))),
        },
      ]}
    >
      {(fields, { add, remove }, { errors }) => (
        <div style={{ marginBottom: 16 }}>
          <Typography.Text strong>{t('cashdesk.split.title')}</Typography.Text>
          <Typography.Paragraph type="secondary" style={{ marginBottom: 8 }}>
            {t('cashdesk.split.hint')}
          </Typography.Paragraph>
          {fields.map((field, index) => (
            <Row key={field.key} gutter={8} align="top">
              <Col xs={24} md={12}>
                <Form.Item
                  name={[field.name, 'categoryId']}
                  label={index === 0 ? t('cashdesk.split.category') : undefined}
                  rules={[{ required: true, message: t('cashdesk.split.categoryRequired') }]}
                >
                  <Select<string>
                    showSearch
                    optionFilterProp="label"
                    options={categories
                      .filter((c) => c.id === lines[index]?.categoryId || !chosen.has(c.id))
                      .map((c) => ({ value: c.id, label: paymentCategoryName(c, t) }))}
                  />
                </Form.Item>
              </Col>
              <Col xs={20} md={10}>
                <Form.Item
                  name={[field.name, 'amount']}
                  label={index === 0 ? t('cashdesk.split.amount') : undefined}
                  rules={[
                    {
                      validator: (_, value: Money | null | undefined) =>
                        value && value.amount > 0
                          ? Promise.resolve()
                          : Promise.reject(new Error(t('cashdesk.split.amountRequired'))),
                    },
                  ]}
                >
                  <MoneyInput currency={currency} minMinor={1} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col xs={4} md={2}>
                <Form.Item label={index === 0 ? ' ' : undefined}>
                  <Button
                    icon={<DeleteOutlined />}
                    aria-label={t('cashdesk.split.remove')}
                    onClick={() => remove(field.name)}
                  />
                </Form.Item>
              </Col>
            </Row>
          ))}
          <Form.ErrorList errors={errors} />
          {fields.length > 0 ? (
            <Typography.Paragraph type={sum === total ? 'secondary' : 'danger'}>
              {t('cashdesk.split.sum', {
                sum: formatMoneyValue(money(sum, currency)),
                total: formatMoneyValue(money(total, currency)),
              })}
            </Typography.Paragraph>
          ) : null}
          <Button
            icon={<PlusOutlined />}
            disabled={fields.length >= Math.min(MAX_SPLIT_LINES, categories.length)}
            onClick={() => add({ amount: total > sum ? money(total - sum, currency) : null })}
          >
            {t('cashdesk.split.add')}
          </Button>
        </div>
      )}
    </Form.List>
  )
}
